import PropTypes from 'prop-types';
import React, { createContext, useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { withRouter, Link } from 'react-router-dom';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { List } from 'immutable';
import classNames from 'classnames';
import { filter, some, debounce, once } from 'lodash';
import { pascalize } from 'humps';

import { track } from '@xanadu/ui-shared/interfaces/woopra';
import { dispatch as gtmDispatch } from '@xanadu/ui-shared/interfaces/googleTagManager';
import Button from '@xanadu/ui-shared/components/widgets/Button';
import {
  userVirtualPageView as gtmUserVirtualPageView
} from '@xanadu/ui-shared/interfaces/googleTagManager/actions';
import { IRELAND_COUNTRY_CODE, UK_COUNTRY_CODE, CANADA_COUNTRY_CODE } from '@xanadu/ui-shared/constants';

import { PRODUCTS, PRODUCT_ROUTES, CANADA_PROVINCES } from '../../constants';
import PHONE_PREFIX_DATA, { PHONE_PREFIX_COUNTRY_CANADA } from '../../data/phonePrefix';

import useValidator from './hooks/useValidator';
import useFieldsReducer from './hooks/useFieldsReducer';
import useLocalisedCountryData from './hooks/useLocalisedCountryData';

import TrustPilotWidget from '../common/TrustPilotWidget';
import PartnerLogos from '../common/PartnerLogos';

import IconClose from '../../../images/icons/misc/icon-close.svg';
import IconMBLogo from '../../../images/mb-branding.svg';

import SignUpForm from './components/SignUpForm';

import {
  getErrorsStep,
  formatDateOfBirth,
  updateSignUpTrialsFlag,
  getSignUpFlag
} from './utils';

import { formatPhoneNumber, STEP_NAMES } from './constants';

import styles from './SignUp.module.scss';
import './transitionGroup.scss';
import { phoneValidationWithSpecialChars } from './validation';

export const SignUpContext = createContext();

const SignUp = (props) => {
  const {
    widthSize,
    createUser,
    resetRegistrationState,
    registrationErrors: signUpErrors,
    registrationLoading,
    securityQuestions,
    currencies,
    countries,
    displayModal,
    isLoggedIn,
    history,
    product,
    promoRegistrationImage,
    displayBrandFeedback,
  } = props;

  const { location: { search: urlSearchParams } } = history;

  const isMobile = widthSize < 1200;
  const [activeStep, setActiveStep] = useState(0);
  const [fields, dispatch] = useFieldsReducer();

  const trustPilotRef = useRef();
  const trustPilotContainerEl = useRef();
  const footerEl = useRef();
  const leftSection = useRef();
  const rightSection = useRef();
  const containerFormEl = useRef();

  const searchParams = new URLSearchParams(urlSearchParams);
  const bonusCode = searchParams.get('bonus-code');

  const {
    'country-id': selectedCountryId = '',
    'country-code': selectedCountryCode = ''
  } = countries.find(c => c.name === fields.country.value) || {};

  const isLastStep = STEP_NAMES[activeStep] === 'join';

  const visibleFields = useMemo(() => (
    filter(fields, (field) => (field.section === STEP_NAMES[activeStep]))
  ), [activeStep, fields]);

  const onValidationResult = useMemo(() => ({ key, error, data }) => {
    dispatch({
      type: 'update',
      payload: {
        key,
        error,
        ...(data) && data,
      }
    });
  }, []);

  const onRegistrationStart = useMemo(() => once(() => {
    track('Registration', 'Registration Started');
    updateSignUpTrialsFlag();
  }), []);

  const { validate } = useValidator(onValidationResult);
  const debounceValidation = useMemo(() => debounce(validate, 400), []);

  const onPropertyChange = ({ name, value, metadata }, options = {}) => {
    const { validate: toValidate = true } = options;
    const { abslength, prefix } = metadata || {};


    const payload = {
      key: name,
      value,
      ...(metadata && { metadata })
    };

    if (toValidate) {
      onRegistrationStart();

      if (name === 'phone-number') {
        const withSpecialChars = phoneValidationWithSpecialChars(prefix);
        debounceValidation(name, value, { abslength, withSpecialChars });
      } else {
        debounceValidation(name, value);
      }
    }

    dispatch({
      type: 'update',
      payload
    });
  };

  const onBatchPropertyUpdate = (fieldsToUpdate) => {
    dispatch({
      type: 'batch-update',
      payload: { fieldsToUpdate }
    });
  };

  const onClose = () => {
    displayModal({ visible: false });
  };

  const onInputBlur = ({ name, value, metadata }) => {
    const { abslength } = metadata || {};
    const {
      'phone-number': { metadata: { prefix: phonePrefix } }
    } = fields;

    const withSpecialChars = phoneValidationWithSpecialChars(phonePrefix);

    validate(name, value, { abslength, withSpecialChars }, { tracking: true });
  };

  const onCreateUser = () => {
    const {
      'currency-id': { value: currency },
      country: { value: countryName },
      'security-question': { value: questionName },
      'phone-number': { value: phoneNumber, metadata: { prefix: phonePrefix } }
    } = fields;

    const {
      id: questionId
    } = securityQuestions.find(({ name }) => (name === questionName)) || {};
    const { 'currency-id': currencyId } = currencies.find(({ id }) => (id === currency)) || {};
    const { 'country-id': countryId } = countries.find(({ name }) => (name === countryName)) || {};

    const computedAddressLine2 = fields['address-line-2'].value
      ? fields['address-line-2'].value.trim()
      : 'n/a';

    const formattedPhoneNumber = formatPhoneNumber(phonePrefix, phoneNumber);

    const provinceOrEmpty = fields.province.value
      ? CANADA_PROVINCES.filter(({ id }) => id === fields.province.value)[0] // this is kinda safe because the dropdown has no arbitrary user inputs
      : {};

    const payload = {
      name: {
        'title-id': 'Mr',
        first: fields.first.value,
        last: fields.last.value
      },
      'date-of-birth': formatDateOfBirth(fields['date-of-birth'].value),
      email: fields.email.value,
      'phone-number': formattedPhoneNumber,
      address: {
        country: {
          'country-id': countryId
        },
        'post-code': fields['post-code'].value.trim(),
        'address-line-1': fields['address-line-1'].value.trim(),
        'address-line-2': computedAddressLine2,
        'region-name': fields.city.value.trim(),
        region: { ...provinceOrEmpty }
      },
      username: fields.username.value,
      password: fields.password.value,
      'password-confirmation': fields.password.value,
      'currency-id': currencyId,
      'user-security-question': {
        question: {
          'security-question-id': questionId
        },
        answer: fields.answer.value
      },
      'forum-handle': '',
      'bonus-code': fields['bonus-code'].value.toUpperCase(),
      'confirm-age': true,
      'confirm-terms': true,
      'deposit-terms': [UK_COUNTRY_CODE, IRELAND_COUNTRY_CODE, CANADA_COUNTRY_CODE].includes(countryId) ? 'HIGH' : null,
      'registration-vertical': product,
    };

    createUser(payload);
  };

  const handleStepClick = step => () => {
    if (step < activeStep) {
      setActiveStep(step);
    }
  };

  const handleNextStepClick = () => {
    if (isLastStep) {
      onCreateUser();
      return;
    }

    const redirectField = visibleFields.find(({
      redirectUrl,
    }) => redirectUrl);

    if (redirectField) {
      displayBrandFeedback(redirectField.redirectUrl);
      return;
    }

    const stepUpdate = activeStep + 1;

    track('Registration', `Registration - Step ${stepUpdate} completed`);

    setActiveStep(stepUpdate);

    gtmDispatch(gtmUserVirtualPageView({
      currentUrl: window.location.href,
      virtualPageTitle: `Step ${stepUpdate + 1} - ${STEP_NAMES[stepUpdate]}`,
      eventName: 'Reg Process',
      virtualPageURL: `/reg/step${stepUpdate + 1}/`,
      previousURL: `/reg/step${stepUpdate}/`,
      userEmail: 'n/a',
      userName: 'n/a',
      userFirstName: 'n/a',
    }));
  };

  const preventDefaultFormBehaviour = (e) => {
    e.preventDefault();
  };

  const getStepClass = useCallback((step) => (
    step === activeStep
      ? classNames({
        [styles.step]: true,
        [styles['step--active']]: true,
      })
      : styles.step
  ), [activeStep]);

  const {
    countryName: detectedCountryName,
    currency: detectedCurrency,
  } = useLocalisedCountryData(countries);

  useEffect(() => {
    const timeoutAnimation = isMobile ? 330 : 420;
    document.documentElement.style.overflow = 'hidden';

    if (rightSection.current) {
      if (!isMobile) {
        rightSection.current.classList.add(styles['section--right--isShown']);
      }

      setTimeout(() => {
        if (rightSection.current && leftSection.current) {
          if (!isMobile) {
            rightSection.current.classList.add(styles['section--right--animate']);
          }

          leftSection.current.classList.add(styles['section--left--animate']);

          if (trustPilotContainerEl.current && footerEl.current) {
            trustPilotContainerEl.current.classList.add(styles['trustPilotBox--show']);
            footerEl.current.classList.add(styles['footer--show']);
          }
        }
      }, timeoutAnimation);
    }

    if (window.Trustpilot && trustPilotRef.current) {
      window.Trustpilot.loadFromElement(trustPilotRef.current, true);
    }

    return () => {
      document.documentElement.style.overflow = '';
      resetRegistrationState();

      track('Registration', 'Registration Drop Off', {
        trials: getSignUpFlag('mb-user-reg-trials'),
        visits: getSignUpFlag('mb-user-returning-times'),
      });
    };
  }, []);

  useEffect(() => {
    if (containerFormEl.current) {
      containerFormEl.current.scrollTo(0, 0);
    }
  }, [activeStep, containerFormEl]);

  useEffect(() => {
    if (selectedCountryCode && selectedCountryId) {
      const selectedPhonePrefix = PHONE_PREFIX_DATA
        .find(phoneCountryOption => phoneCountryOption[1] === selectedCountryCode.toLowerCase())
        || PHONE_PREFIX_DATA[PHONE_PREFIX_DATA.length - 1];

      if (selectedPhonePrefix) {
        onPropertyChange({
          name: 'phone-number',
          metadata: {
            prefix: selectedPhonePrefix[2],
            abslength: selectedPhonePrefix[3]
          }
        }, { validate: false });
      }
    }
  }, [selectedCountryCode, selectedCountryId, UK_COUNTRY_CODE]);

  useEffect(() => {
    const isCountryEligible = countries.some(c => c.name === detectedCountryName);

    if (detectedCountryName && isCountryEligible) {
      onPropertyChange({
        name: 'country',
        value: detectedCountryName,
      }, { validate: false });
    }

    if (detectedCurrency) {
      onPropertyChange({
        name: 'currency-id',
        value: detectedCurrency,
      }, { validate: false });
    }
  }, [detectedCountryName, detectedCurrency]);

  useEffect(() => {
    if (signUpErrors.size) {
      const errorsStep = getErrorsStep(signUpErrors, fields);
      setActiveStep(errorsStep);

      dispatch({
        type: 'update-errors',
        payload: { signUpErrors }
      });
    }
  }, [signUpErrors]);

  useEffect(() => {
    if (bonusCode) {
      onPropertyChange({ name: 'bonus-code', value: bonusCode });
    }
  }, [bonusCode]);

  useEffect(() => {
    if (isLoggedIn) {
      setTimeout(() => {
        onClose();

        // displayModal({
        //   show: true,
        //   section: 'kyc-documents-warning',
        //   stepNumber: activeStep,
        // });
      }, 640);
    }
  }, [isLoggedIn, activeStep]);

  const buttonDisabled = some(visibleFields, ({
    error,
    value,
    isRequired,
    redirectUrl,
  }) => (!!error && !redirectUrl) || !value && isRequired);

  const mainError = useMemo(() => {
    const { messages = [] } = signUpErrors.size === 1 && !signUpErrors.first().field
      ? signUpErrors.first()
      : {};

    return messages[0];
  }, [signUpErrors]);

  const productHomeUrl = PRODUCT_ROUTES[product];
  const productName = product.replace('-', ' ');

  const signUpImageKey = `joinNowImage${product === PRODUCTS.exchange ? '' : pascalize(product.toLowerCase())}`;

  const signupImageStyle = { backgroundImage: `${promoRegistrationImage ? `url(${promoRegistrationImage})` : `url(${props[signUpImageKey]})`}` };

  const animationClass = 'sliding-normal';
  const mainClass = classNames({
    SignUp: true,
    [styles.main]: true
  });

  const leftSectionClass = classNames({
    [styles.section]: true,
    [styles['section--left']]: true,
    [styles['section--left--animateExit']]: isLoggedIn && !isMobile && rightSection.current && leftSection.current
  });

  const rightSectionClass = classNames({
    [styles.section]: true,
    [styles['section--right']]: true,
    [styles['section--right--animateExit']]: isLoggedIn && !isMobile && rightSection.current && leftSection.current
  });

  const button = (
    <Button
      onClick={handleNextStepClick}
      sizeType="wider"
      buttonType="primary"
      disabled={buttonDisabled}
      loading={registrationLoading}
      className={styles.nextButton}
      dataHook="register-next-step"
    >
      {isLastStep ? 'Join Matchbook' : 'Next'}
    </Button>
  );

  return (
    <div className={mainClass}>
      <Link
        to={productHomeUrl}
        className={styles.logoContainer}
        data-hook="mb-home-logo"
      >
        <span className={styles.logo}>
          <IconMBLogo />
        </span>
        <div className={styles.productName}>
          <span>{productName}</span>
        </div>
      </Link>
      <button
        data-hook="register-close"
        className={styles.closeButton}
        onClick={onClose}
      >
        <span>Close</span>
        <IconClose />
      </button>
      <div className={styles.content}>
        <div className={styles.sections}>
          <div className={leftSectionClass} ref={leftSection}>
            <h1>Join Matchbook</h1>
            <div className={styles.steps}>
              <button className={getStepClass(0)} onClick={handleStepClick(0)}>
                1. Account
              </button>
              <button className={getStepClass(1)} onClick={handleStepClick(1)}>
                2. Address
              </button>
              <button className={getStepClass(2)} onClick={handleStepClick(2)}>
                3. Join
              </button>
            </div>
            <div
              className={styles.containerForm}
              ref={containerFormEl}
            >
              <TransitionGroup
                className={styles.form}
                childFactory={child => React.cloneElement(child, { classNames: animationClass })}
                onSubmit={preventDefaultFormBehaviour}
                component="form"
              >
                <CSSTransition key={activeStep} in timeout={440} classNames={animationClass}>
                  <div className={styles.formSection}>
                    <SignUpContext.Provider
                      value={{
                        onPropertyChange,
                        onBatchPropertyUpdate,
                        onInputBlur,
                        currencies,
                        countries,
                        securityQuestions,
                        isMobile,
                        product,
                        provinces: CANADA_PROVINCES,
                        countryId: selectedCountryId
                      }}
                    >
                      <SignUpForm
                        fields={fields}
                        activeStep={activeStep}
                      />
                      {isMobile && button}
                      {!!mainError && isMobile && (
                        <span className={styles.mainErrorMessage}>
                          {mainError}
                        </span>
                      )}
                    </SignUpContext.Provider>
                    {isMobile && (
                      <div className={styles.trustPilotBox} ref={trustPilotContainerEl}>
                        <span>{"The UK's most trusted Betting Exchange"}</span>
                        <TrustPilotWidget trustBoxRef={trustPilotRef} />
                      </div>
                    )}
                    {isMobile && (
                      <div className={styles.footer} ref={footerEl}>
                        <PartnerLogos />
                      </div>
                    )}
                  </div>
                </CSSTransition>
              </TransitionGroup>
              {!isMobile && button}
            </div>
          </div>
          <div
            ref={rightSection}
            className={rightSectionClass}
            style={signupImageStyle}
          />
        </div>
        {!!mainError && !isMobile && (
          <span className={styles.mainErrorMessage}>
            {mainError}
          </span>
        )}
        {!isMobile && (
          <div className={styles.footer} ref={footerEl}>
            <PartnerLogos />
          </div>
        )}
      </div>
      {!isMobile && (
        <div className={styles.trustPilotBox} ref={trustPilotContainerEl}>
          <span>{"The UK's most trusted Betting Exchange"}</span>
          <TrustPilotWidget trustBoxRef={trustPilotRef} />
        </div>
      )}
    </div>
  );
};

SignUp.propTypes = {
  currencies: PropTypes.instanceOf(List).isRequired,
  securityQuestions: PropTypes.instanceOf(List).isRequired,
  countries: PropTypes.instanceOf(List).isRequired,
  registrationLoading: PropTypes.bool.isRequired,
  registrationErrors: PropTypes.instanceOf(List).isRequired,
  resetRegistrationState: PropTypes.func.isRequired,
  displayModal: PropTypes.func.isRequired,
  widthSize: PropTypes.number.isRequired,
  createUser: PropTypes.func.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  history: PropTypes.shape({
    location: PropTypes.shape({
      search: PropTypes.string
    }).isRequired
  }).isRequired,
  product: PropTypes.string.isRequired,
  promoRegistrationImage: PropTypes.string,
  displayBrandFeedback: PropTypes.func.isRequired,
};

export default withRouter(SignUp);
